import {Component, Input, OnInit} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import {
  HttpClient,
  HttpErrorResponse, HttpHeaders
} from '@angular/common/http';
import {catchError} from 'rxjs/operators';
import {throwError} from 'rxjs';

import {Contact} from '../../../core/models/contact';
import {Listing} from '../../../core/models/listing';
import {Globals} from '../../../core/services/globals';
import {CommonModule} from "@angular/common";
import {environment} from "../../../../environments/environment";

// Define interface for form errors
interface FormControlErrors {
  required?: boolean;
  minlength?: { requiredLength: number; actualLength: number };
  email?: boolean;
}

@Component({
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  standalone: true,
  selector: 'contact-form',
  templateUrl: './contact-form.component.html'
})
export class ContactFormComponent {
  @Input() selectedListing?: Listing;

  public title = '';
  public errorStr = '';
  public successBln = false;
  public hiddenBln = false;
  public isFormSubmitted = false;


  contactForm: FormGroup;
  model = new Contact('', '', '', '');

  constructor(
    private httpClient: HttpClient,
    private formBuilder: FormBuilder,
    public globals: Globals
  ) {
    this.contactForm = this.createForm();
  }

  private createForm(): FormGroup {
    return this.formBuilder.group({
      name: [this.model.name, [
        Validators.required,
        Validators.minLength(4)
      ]],
      email: [this.model.email, [
        Validators.required,
        Validators.email
      ]],
      phone: [this.model.phone, [
        Validators.required,
        Validators.minLength(4)
      ]],
      message: [this.model.message, [
        Validators.required,
        Validators.minLength(4)
      ]]
    });
  }

  // Methods for form control validation state
  isNameTouched(): boolean {
    return this.contactForm.get('name')?.touched ?? false;
  }

  isNameDirty(): boolean {
    return this.contactForm.get('name')?.dirty ?? false;
  }

  isNameInvalid(): boolean {
    return this.contactForm.get('name')?.invalid ?? false;
  }

  isEmailTouched(): boolean {
    return this.contactForm.get('email')?.touched ?? false;
  }

  isEmailDirty(): boolean {
    return this.contactForm.get('email')?.dirty ?? false;
  }

  isEmailInvalid(): boolean {
    return this.contactForm.get('email')?.invalid ?? false;
  }

  isPhoneTouched(): boolean {
    return this.contactForm.get('phone')?.touched ?? false;
  }

  isPhoneDirty(): boolean {
    return this.contactForm.get('phone')?.dirty ?? false;
  }

  isPhoneInvalid(): boolean {
    return this.contactForm.get('phone')?.invalid ?? false;
  }

  isMessageTouched(): boolean {
    return this.contactForm.get('message')?.touched ?? false;
  }

  isMessageDirty(): boolean {
    return this.contactForm.get('message')?.dirty ?? false;
  }

  isMessageInvalid(): boolean {
    return this.contactForm.get('message')?.invalid ?? false;
  }

  public submitContactForm(): void {
    this.isFormSubmitted = true;

    if (this.contactForm.invalid || !this.selectedListing) {
      this.errorStr = 'Please fill out all required fields correctly.';
      return;
    }

    const contactData = this.generateContactData();

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${environment.apiKey}`
      })
    };

    this.httpClient.post(
      environment.apiUrls.contactEnquiry,
      contactData,
      httpOptions
    )
      .pipe(
        catchError(this.handleError)
      )
      .subscribe({
        next: () => {
          this.trackAnalyticsEvent();
          this.successBln = true;
          this.errorStr = '';
        },
        error: (err: HttpErrorResponse) => {
          this.errorStr = 'Failed to submit form. Please try again.';
          console.error('Submission failed', err);
        }
      });
  }


  private generateContactData(): ContactSubmissionData {
    return {
      name: this.contactForm.get('name')?.value ?? '',
      email: this.contactForm.get('email')?.value ?? '',
      phone: this.contactForm.get('phone')?.value ?? '',
      comments: this.contactForm.get('message')?.value ?? '',
      refer: 'CM',
      source: this.selectedListing?.sourceStr ?? '',
      mlsNum: this.selectedListing?.MLSNUM ?? '',
      action: 'property'
    } as ContactSubmissionData;
  }


  private trackAnalyticsEvent(): void {
    try {
      const utmParams = {
        'event': 'form_submit',
        'utm_source': this.globals.utmSource ?? null,
        'utm_medium': this.globals.utmMedium ?? null,
        'utm_campaign': this.globals.utmCampaign ?? null,
        'utm_term': this.globals.utmTerm ?? null,
        'utm_content': this.globals.utmContent ?? null,
        'hitCallback': () => {
          console.log('Event received');
        }
      };

      (window as any).dataLayer?.push(utmParams);
    } catch (e) {
      console.error('Analytics tracking failed', e);
    }
  }

  public hideForm(): void {
    this.hiddenBln = true;
  }

  public resetFormSubmission(): void {
    this.isFormSubmitted = false;
  }


  private handleError(error: HttpErrorResponse) {
    console.error('An error occurred', error);
    return throwError(() => new Error(error.message || 'Unknown error'));
  }

  // Helper method to safely get form control errors
  getControlErrors(controlName: string): FormControlErrors | null {
    const control = this.contactForm.get(controlName);
    return control?.errors as FormControlErrors | null;
  }

  // Method to check specific error type
  hasError(controlName: string, errorType: string): boolean {
    const errors = this.getControlErrors(controlName);
    return errors ? !!errors[errorType as keyof FormControlErrors] : false;
  }
}

// Type definition for contact submission data
interface ContactSubmissionData {
  [key: string]: string | number | boolean;

  name: string;
  email: string;
  phone: string;
  comments: string;
  refer: string;
  source: string;
  mlsNum: string;
  action: string;
}


